import React, { useEffect, useMemo } from "react";
import { Form, Field, ErrorMessage } from "formik";
import SelectField from "components/form/Select/SelectField";
import CustomInputNumberField from "components/form/CustomInputNumber/CustomInputNumberField";
import ErrorMessageWithIcon from "./ErrorMessageWithIcon";
import Button from "components/Button/Button";
import styles from "./Orders.module.scss";
import { IUser } from "types";
import { USER_TYPE } from "constants/enums";

interface OrderFormProps {
  isSubmitting: boolean;
  values: any;
  errors: any;
  touched: any;
  contractOptions: any[];
  periodOptions: any[];
  orderTypeOptions: any[];
  returnTypeOptions: any[];
  setFieldValue: any;
  profile: IUser | null;
  onMinAmountChange: any;
  minReplenishmentAmount: number;
  minReturnAmount: number;
}

const OrderForm: React.FC<OrderFormProps> = ({
  isSubmitting,
  values,
  errors,
  touched,
  contractOptions,
  periodOptions,
  orderTypeOptions,
  returnTypeOptions,
  setFieldValue,
  profile,
  onMinAmountChange,
  minReplenishmentAmount,
  minReturnAmount,
}) => {
  

  const showPeriodField =
    profile?.type === USER_TYPE.FL && values.orderType === "2ndfl";

    useEffect(() => {
      if (values.selectedContract) {
        const selectedContract = contractOptions.find(
          (option) => option.value === values.selectedContract
        );
    
        if (selectedContract && selectedContract.organization) {
          
          setFieldValue("replenishmentAmount", minReplenishmentAmount, false);
          setFieldValue("returnAmount", minReturnAmount, false);

          onMinAmountChange(minReplenishmentAmount, minReturnAmount);
        }
      }
    }, [
      values.selectedContract,
      contractOptions,
      setFieldValue,
      onMinAmountChange,
      minReplenishmentAmount,
      minReturnAmount,       
    ]);
    

  const filteredOrderTypeOptions = useMemo(() => {
    if (profile?.type === USER_TYPE.FL) {
      return orderTypeOptions;
    }

    return orderTypeOptions.filter((option) => option.value !== "2ndfl");
  }, [orderTypeOptions, profile]);

  return (
    <Form className={styles.wrapper}>
      <div>
        <Field
          as={SelectField}
          label="Тип заявки"
          name="orderType"
          options={filteredOrderTypeOptions}
          className={`${styles.select} ${
            errors.orderType && touched.orderType ? styles.selectWithError : ""
          }`}
        />
        <ErrorMessage
          name="orderType"
          component="div"
          className={styles.errorMessage}
        />
      </div>

      <div>
        {showPeriodField && (
          <div className={styles.selectContainer}>
            <Field
              as={SelectField}
              label="Период"
              name="selectedPeriod"
              options={periodOptions}
              className={`${styles.select} ${
                errors.selectedPeriod && touched.selectedPeriod
                  ? styles.selectWithError
                  : ""
              }`}
            />
            <ErrorMessageWithIcon
              name="selectedPeriod"
              className={styles.errorMessage}
            />
          </div>
        )}

        {values.orderType === "contractRefill" && (
          <>
            <div className="fieldRow">
              <div className={styles.selectContainer}>
                <Field
                  as={SelectField}
                  label="Договор"
                  name="selectedContract"
                  options={contractOptions}
                  className={`${styles.select} ${
                    errors.selectedContract && touched.selectedContract
                      ? styles.selectWithError
                      : ""
                  }`}
                />
                <ErrorMessageWithIcon
                  name="selectedContract"
                  className={styles.errorMessage}
                />
              </div>
            </div>

            <div className="fieldRow">
              <div className={styles.selectContainer}>
                <Field
                  as={CustomInputNumberField}
                  label="Сумма"
                  name="replenishmentAmount"
                  format="####"
                  min={minReplenishmentAmount}
                  className={
                    errors.replenishmentAmount && touched.replenishmentAmount
                      ? styles.selectWithError
                      : ""
                  }
                />

                <ErrorMessageWithIcon
                  name="replenishmentAmount"
                  className={styles.errorMessage}
                />
              </div>
            </div>
          </>
        )}

        {values.orderType === "earlyReturn" && (
          <div>
            <div className="fieldRow">
              <div className={styles.selectContainer}>
                <Field
                  as={SelectField}
                  label="Возврат"
                  name="returnType"
                  options={returnTypeOptions}
                  className={`${styles.select} ${
                    errors.returnType && touched.returnType
                      ? styles.selectWithError
                      : ""
                  }`}
                />
                <ErrorMessage
                  name="returnType"
                  component="div"
                  className={styles.errorMessage}
                />
              </div>
            </div>

            {values.returnType === "fullLoan" && (
              <div className="fieldRow">
                <div className={styles.selectContainer}>
                  <Field
                    as={SelectField}
                    label="Договор"
                    name="selectedContract"
                    options={contractOptions}
                    className={`${styles.select} ${
                      errors.selectedContract && touched.selectedContract
                        ? styles.selectWithError
                        : ""
                    }`}
                  />
                  <ErrorMessageWithIcon
                    name="selectedContract"
                    className={styles.errorMessage}
                  />
                </div>
              </div>
            )}

            {values.returnType === "partialReturn" && (
              <>
                <div className="fieldRow">
                  <div className={styles.selectContainer}>
                    <Field
                      as={SelectField}
                      label="Договор"
                      name="selectedContract"
                      options={contractOptions}
                      className={`${styles.select} ${
                        errors.selectedContract && touched.selectedContract
                          ? styles.selectWithError
                          : ""
                      }`}
                    />
                    <ErrorMessageWithIcon
                      name="selectedContract"
                      className={styles.errorMessage}
                    />
                  </div>
                </div>

                <div className="fieldRow">
                  <div className={styles.selectContainer}>
                    <Field
                      as={CustomInputNumberField}
                      label="Сумма возврата"
                      name="returnAmount"
                      format="####"
                      className={`${styles.error} ${
                        errors.returnAmount && touched.returnAmount
                          ? styles.selectWithError
                          : ""
                      }`}
                    />
                    <ErrorMessageWithIcon
                      name="returnAmount"
                      className={styles.errorMessage}
                    />
                  </div>
                </div>
              </>
            )}

            {values.returnType === "capitalizedInterest" && (
              <div className="fieldRow">
                <div className={styles.selectContainer}>
                  <Field
                    as={CustomInputNumberField}
                    label="Сумма возврата"
                    name="capitalizedInterestAmount"
                    format="####"
                    className={`${styles.error} ${
                      errors.capitalizedInterestAmount &&
                      touched.capitalizedInterestAmount
                        ? styles.selectWithError
                        : ""
                    }`}
                  />
                  <ErrorMessageWithIcon
                    name="capitalizedInterestAmount"
                    className={styles.errorMessage}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <Button
          className={styles.submitBtn}
          type="primary"
          htmlType="submit"
          disabled={isSubmitting}
        >
          Отправить заявку
        </Button>
      </div>
    </Form>
  );
};

export default OrderForm;
